/* eslint-disable indent */
import {
  AddNewReport,
  AddNewReportParams,
  ReportExtract,
  ValidationErrorResponse,
} from 'domain/usecases/add-new-report'
import { ConfirmNewPatientReport, ConfirmNewPatientReportParams } from 'domain/usecases/confirm-new-patient-report'
import { ConfirmPatientReport, ConfirmPatientReportParams } from 'domain/usecases/confirm-patient-report'
import { GetReport, GetReportParams, Report, ReportContentReviewedParams, Treatment } from 'domain/usecases/get-report'
import {
  GetReportComments,
  GetReportCommentsParams,
  GetReportCommentsResponse,
} from 'domain/usecases/get-report-comments'
import { GetReportPdfLink } from 'domain/usecases/get-report-pdf-link'
import {
  GetReportPermissions,
  GetReportPermissionsParams,
  GetReportPermissionsResponse,
} from 'domain/usecases/get-report-permissions'
import {
  ReplyToOncologist,
  ReplyToOncologistParams,
  ReplyToOncologistResponse,
} from 'domain/usecases/reply-to-oncologist'
import { SelectTreatment, SelectTreatmentParams } from 'domain/usecases/select-treatment'
import { ShareReportParams } from 'domain/usecases/share-report'
import { baseUrl, entitiesUrl, insightsUrl, reportsUrl } from 'infra/config/aws-env'
import { HttpClient } from 'infra/http/usecases/http-client'
import { GeneDetailDto, GetGeneDetail, GetGeneDetailParams } from './usecases/get-gene-detail'
import { ReportAsRead, ReportAsReadParams } from './usecases/report-as-read'
import { EditReportProps } from 'domain/usecases/edit-report'
import { AddGenomicVariantParams } from 'domain/usecases/add-genomic-variant'
import { AddTreatmentParams } from 'domain/usecases/add-treatment'
import { DeleteGenomicVariantParams, DeleteGenomicVariant } from 'domain/usecases/delete-genomic-variant'
import { DeleteTreatmentParams, DeleteTreatment } from 'domain/usecases/delete-treatment'
import { ListReportsParams, ReportsItemDto } from 'domain/usecases/list-reports'


export class ReportsService
  implements
  AddNewReport,
  ConfirmPatientReport,
  ConfirmNewPatientReport,
  GetReport,
  GetReportPdfLink,
  GetGeneDetail,
  SelectTreatment,
  ReportAsRead,
  GetReportPermissions,
  ReplyToOncologist,
  GetReportComments,
  DeleteGenomicVariant,
  DeleteTreatment {
  constructor(private readonly httpClient: HttpClient) { }

  getReports(params: Partial<ListReportsParams>): Promise<ReportsItemDto> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .request({
          headers: {
            'Content-Type': 'application/json',
          },
          type: 'get',
          url: `${baseUrl}${reportsUrl.base}${this.getQueryString(params)}`,
          body: params,
        })
        .then((result) => resolve(result))
        .catch(() => reject())
    })
  }

  private getQueryString(params: Partial<ListReportsParams>) {
    const queryString = []
    if (params.search) queryString.push(`search=${params.search}`)
    if (params.page) queryString.push(`page=${params.page}`)
    if (params.oncologist_id) queryString.push(`oncologist_id=${params.oncologist_id}`)
    if (params.size) queryString.push(`size=${params.size}`)
    if (params.status && params.status.length > 0) queryString.push(`patient_status=${params.status?.join('&patient_status=')}`)
    if (params.report_status && params.report_status.length > 0) queryString.push(`status=${params.report_status?.join('&status=')}`)
    if (params.name) queryString.push(`name=${params.name}`)
    if (params.treatment && params.treatment.length > 0) queryString.push(`treatment=${params.treatment?.join('&treatment=')}`)
    if (params.cancer_type && params.cancer_type.length > 0) queryString.push(`cancer_type=${params.cancer_type?.join('&cancer_type=')}`)
    if (params.genomic_variant && params.genomic_variant.length > 0) queryString.push(`genomic_variant=${params.genomic_variant?.join('&genomic_variant=')}`)
    if (params.order_by?.field === 'changed' as keyof Report) queryString.push(`order_by_changed=${params.order_by.order}`)
    if (params.order_by?.field === 'name_asc' as keyof Report) queryString.push('order_by_name=ASC')
    if (params.order_by?.field === 'name_desc' as keyof Report) queryString.push('order_by_name=DESC')
    if (params.order_by?.field === 'oncologist_asc' as keyof Report) queryString.push('order_by_oncologist=ASC')
    if (params.order_by?.field === 'oncologist_desc' as keyof Report) queryString.push('order_by_oncologist=DESC')

    return `?${queryString.join('&')}`
  }

  addNewReport(params: AddNewReportParams): Promise<ReportExtract | ValidationErrorResponse> {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      formData.append('report_lab', params.lab)

      this.httpClient
        .request({
          type: 'post',
          url: `${baseUrl}${reportsUrl.base}/presigned`,
          body: formData,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((data) => {
          const slashIndex = data.presigned_url.upload_key.indexOf('/')
          const reportId = data.presigned_url.upload_key.substring(slashIndex + 1)
          resolve({ ...data, report_id: reportId })
        })
        .catch((error) => reject(error))
    })
  }

  uploadReportFile(presigned_url: string, file: File) {
    return new Promise((resolve, reject) => {
      this.httpClient.request({
        type: 'post',
        url: presigned_url,
        body: file,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }


  confirmPatientReport(params: ConfirmPatientReportParams): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .request({
          type: 'put',
          url: `${baseUrl}${reportsUrl.base}/${params.reportId}/confirm-patient`,
          body: {
            MRN: params.MRN,
            name: params.name,
            oncologistId: params.oncologistId,
          },
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  confirmNewPatientReport(params: ConfirmNewPatientReportParams): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .request({
          type: 'post',
          url: `${baseUrl}${reportsUrl.base}/${params.reportId}/confirm-patient`,
          body: {
            MRN: params.MRN,
            name: params.name,
            oncologistId: params.oncologistId,
          },
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  getReport(params: GetReportParams): Promise<Report> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .request({
          type: 'get',
          headers: {
            'Content-type': 'application/json',
          },
          url: `${baseUrl}${reportsUrl.base}/${params.reportId}`,
        })
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  getReportPdfLink(params: { reportId: string }): Promise<string> {
    return new Promise((resolve) => {
      this.httpClient
        .request({
          type: 'get',
          headers: {
            'Content-type': 'application/json',
          },
          url: `${baseUrl}${reportsUrl.base}/${params.reportId}/pdf`,
        })
        .then((link: { url: string }) => resolve(link.url))
        .catch(() => resolve(''))
    })
  }

  getGeneDetail(params: GetGeneDetailParams): Promise<GeneDetailDto> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .request({
          type: 'get',
          headers: {
            'Content-type': 'application/json',
          },
          url: `${baseUrl}${reportsUrl.base}/${params.reportId}/genomic-variant/${params.geneId}`,
        })
        .then((data) => resolve(data))
        .catch((err) => reject(err))
    })
  }

  selectTreatment(props: SelectTreatmentParams): Promise<unknown> {
    const newTreatments: { id: number }[] = []
    const newProps: SelectTreatmentParams = JSON.parse(JSON.stringify(props))

    props.treatments.map((t) => {
      if (!newTreatments.find((n) => n.id === t.id)) newTreatments.push(t)
    })

    newProps.treatments = newTreatments as Treatment[]

    return new Promise<void>((resolve, reject) => {
      this.httpClient
        .request({
          type: 'post',
          headers: {
            'Content-Type': 'application/json',
          },
          url: `${baseUrl}${reportsUrl.base}/${props.reportId}/select-treatment`,
          body: newProps,
        })
        .then(() => resolve())
        .catch(() => reject())
    })
  }

  markReportAsread(params: ReportAsReadParams): Promise<{ [key: string]: string }> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .request({
          headers: {
            'Content-Type': 'application/json',
          },
          type: 'post',
          url: `${baseUrl}${reportsUrl.base}/${params.MRN}/read`,
          body: {
            read: params.read,
          },
        })
        .then((result) => resolve(result))
        .catch(() => reject())
    })
  }

  shareReport(params: ShareReportParams): Promise<void> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .request({
          headers: {
            'Content-Type': 'application/json',
          },
          type: 'post',
          url: `${baseUrl}${reportsUrl.base}/${params.reportId}/share`,
          body: {
            users: params.users,
            message: params.message,
          },
        })
        .then((result) => resolve(result))
        .catch(() => reject())
    })
  }

  getReportPermissions(params: GetReportPermissionsParams): Promise<GetReportPermissionsResponse> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .request({
          type: 'get',
          headers: {
            'Content-type': 'application/json',
          },
          url: `${baseUrl}${reportsUrl.base}/${params.reportId}/permissions`,
        })
        .then((data) => resolve(data))
        .catch((err) => reject(err))
    })
  }

  replyToOncologist(params: ReplyToOncologistParams): Promise<ReplyToOncologistResponse> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .request({
          headers: {
            'Content-Type': 'application/json',
          },
          type: 'post',
          url: `${baseUrl}${reportsUrl.base}/${params.reportId}/comments`,
          body: {
            comment: params.comment,
          },
        })
        .then((result) => resolve(result))
        .catch(() => reject())
    })
  }

  getReportComments(params: GetReportCommentsParams): Promise<GetReportCommentsResponse> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .request({
          type: 'get',
          headers: {
            'Content-type': 'application/json',
          },
          url: `${baseUrl}${reportsUrl.base}/${params.reportId}/comments`,
        })
        .then((data) => resolve(data))
        .catch((err) => reject(err))
    })
  }

  editReport(props: Partial<EditReportProps>): Promise<Report> {
    return new Promise((resolve, reject) => {
      this.httpClient
        .request({
          type: 'patch',
          headers: {
            'Content-Type': 'application/json',
          },
          url: `${baseUrl}${reportsUrl.extracted}/${props.report_id}`,
          body: props,
        })
        .then((patient) => resolve(patient))
        .catch((error) => {
          const getError = error && error.detail && error.detail[0]

          reject(getError.msg)
        })
    })
  }

  addGenomicVariant(params: AddGenomicVariantParams): Promise<AddGenomicVariantParams> {
    return new Promise((resolve, reject) => {
      this.httpClient.request({
        type: 'post',
        url: `${baseUrl}${entitiesUrl.base}/genomic_variant?report_id=${params.report_id}`,
        body: params.genomic_variant,
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  updateGenomicVariant(params: AddGenomicVariantParams): Promise<AddGenomicVariantParams> {
    return new Promise((resolve, reject) => {
      this.httpClient.request({
        type: 'put',
        url: `${baseUrl}${entitiesUrl.base}/genomic_variant?genomic_variant_id=${params.genomic_variant.id}&report_id=${params.report_id}`,
        body: params.genomic_variant,
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  deleteGenomicVariant(params: DeleteGenomicVariantParams): Promise<DeleteGenomicVariantParams> {
    return new Promise((resolve, reject) => {
      this.httpClient.request({
        type: 'delete',
        url: `${baseUrl}${entitiesUrl.base}/genomic_variant?genomic_variant_id=${params.genomic_variant_id}&report_id=${params.report_id}`,
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  sugestGenomicVariantsInDB() {
    return new Promise((resolve, reject) => {
      this.httpClient.request({
        type: 'get',
        url: `${baseUrl}${insightsUrl.base}/global_genomic_variants_list`,
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  addTreatment(params: AddTreatmentParams): Promise<AddTreatmentParams> {
    return new Promise((resolve, reject) => {
      this.httpClient.request({
        type: 'post',
        url: `${baseUrl}${entitiesUrl.base}/add_treatment?report_id=${params.report_id}`,
        body: params.therapy,
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  deleteTreatment(params: DeleteTreatmentParams): Promise<DeleteTreatmentParams> {
    return new Promise((resolve, reject) => {
      this.httpClient.request({
        type: 'delete',
        url: `${baseUrl}${entitiesUrl.base}/treatment?report_id=${params.report_id}&treatment_id=${params.treatment_id}`,
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  uploadReportPDF(presigned_url: string, file: File) {
    return new Promise((resolve, reject) => {
      this.httpClient.putPresignedUrl(presigned_url, file)
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  markReportContentAsReviewed(params: ReportContentReviewedParams): Promise<{ [key: string]: string }> {
    return new Promise((resolve, reject) => {
      this.httpClient.request({
        headers: {
          'Content-Type': 'application/json',
        },
        type: 'post',
        url: `${baseUrl}${reportsUrl.base}/${params.reportId}/content-reviewed`,
        body: {
          reviewed: params.reviewed,
        },
      })
        .then((result) => resolve(result))
        .catch(() => reject())
    })
  }

  getReportContentReviewedStatus(reportId: string): Promise<ReportContentReviewedParams> {
    return new Promise((resolve, reject) => {
      this.httpClient.request({
        type: 'get',
        url: `${baseUrl}${reportsUrl.base}/${reportId}/content-reviewed`,
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

}
